import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";

/**
 * Compact sign-in / sign-up modal. Used before checkout.
 */
export default function AuthModal({ open, onClose, onAuthed, initialMode = "signin" }) {
  const [mode, setMode] = useState(initialMode);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [busy, setBusy] = useState(false);
  const { login, signup } = useAuth();

  // Reset the mode each time the modal is opened so it reflects the current entry point
  useEffect(() => {
    if (open) setMode(initialMode);
  }, [open, initialMode]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "signup") {
        await signup(email.trim(), password, firstName.trim());
        toast.success("Account created.");
      } else {
        await login(email.trim(), password);
        toast.success("Signed in.");
      }
      onAuthed?.();
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Authentication failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={onClose}
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          data-testid="auth-modal"
        >
          <motion.form
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 20, opacity: 0 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            onSubmit={submit}
            className="w-full max-w-md bg-[#0A0A14] border border-white/10 p-6 md:p-8"
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-anton uppercase text-3xl">
                {mode === "signup" ? "Create account" : "Sign in"}
              </h2>
              <button type="button" onClick={onClose} aria-label="Close" data-testid="auth-modal-close" className="text-white/60 hover:text-white">
                <X size={18} />
              </button>
            </div>

            {mode === "signup" && (
              <div className="mb-6">
                <label className="text-[10px] tracking-[0.32em] uppercase text-white/50">First Name</label>
                <input
                  type="text"
                  className="input-line focus-ring"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  required
                  data-testid="auth-input-name"
                />
              </div>
            )}
            <div className="mb-6">
              <label className="text-[10px] tracking-[0.32em] uppercase text-white/50">Email</label>
              <input
                type="email"
                className="input-line focus-ring"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                required
                data-testid="auth-input-email"
              />
            </div>
            <div className="mb-8">
              <label className="text-[10px] tracking-[0.32em] uppercase text-white/50">Password</label>
              <input
                type="password"
                className="input-line focus-ring"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete={mode === "signup" ? "new-password" : "current-password"}
                minLength={mode === "signup" ? 10 : 1}
                required
                data-testid="auth-input-password"
              />
              {mode === "signup" ? (
                <p className="mt-2 text-[11px] text-white/40">Minimum 10 characters, letters + numbers.</p>
              ) : (
                <div className="mt-2 text-right">
                  <a
                    href="/forgot-password"
                    className="text-[11px] tracking-[0.22em] uppercase text-white/50 hover:text-[#2A5BFF]"
                    data-testid="auth-forgot-password"
                  >
                    Forgot password?
                  </a>
                </div>
              )}
            </div>

            <button
              type="submit"
              disabled={busy}
              className="btn-primary w-full px-6 py-4 text-[12px] tracking-[0.22em] uppercase font-medium disabled:opacity-60"
              data-testid="auth-submit"
            >
              {busy ? "…" : mode === "signup" ? "Create account" : "Sign in"}
            </button>

            <div className="mt-6 text-center text-sm text-white/50">
              {mode === "signup" ? (
                <>Already have an account?{" "}
                  <button type="button" className="text-[#2A5BFF] hover:text-white" onClick={() => setMode("signin")} data-testid="auth-toggle-signin">Sign in</button>
                </>
              ) : (
                <>New to BRep?{" "}
                  <button type="button" className="text-[#2A5BFF] hover:text-white" onClick={() => setMode("signup")} data-testid="auth-toggle-signup">Create an account</button>
                </>
              )}
            </div>
          </motion.form>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
